import { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { ArrowRight, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import Autoplay from 'embla-carousel-autoplay';
import '@/App.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const makeSlug = (n) => (n || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

function useInView(ref) {
  const [v, setV] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setV(true); }, { threshold: 0.1 });
    o.observe(ref.current);
    return () => o.disconnect();
  }, [ref]);
  return v;
}

function Reveal({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const v = useInView(ref);
  return <div ref={ref} className={`${className} transition-all duration-700 ${v ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: `${delay}ms` }}>{children}</div>;
}

export default function Products() {
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('filter') || '');

  useEffect(() => {
    axios.get(`${API}/products`).then(r => setProducts(r.data)).catch(() => {});
    axios.post(`${API}/analytics/visit`, { page: '/products' }).catch(() => {});
  }, []);

  useEffect(() => {
    const f = searchParams.get('filter');
    if (f) setSearch(f);
  }, [searchParams]);

  const filtered = products.filter(p => p.name.toLowerCase().includes(search.toLowerCase()) || p.category?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="products-page">
      {/* Hero */}
      <section className="py-20 sm:py-28 bg-secondary/40">
        <div className="container mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <p className="text-sm text-muted-foreground uppercase tracking-widest mb-3">{t('products.category')}</p>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-light">{t('products.title')}</h1>
            <div className="accent-line mx-auto mt-4" />
            <p className="text-base text-muted-foreground mt-4 max-w-xl mx-auto">{t('products.subtitle')}</p>
          </Reveal>
        </div>
      </section>

      {/* Featured Slider */}
      <section className="py-12 sm:py-16 border-b border-border/50">
        <div className="container mx-auto px-5 sm:px-8">
          <Carousel opts={{ loop: true, align: 'start' }} plugins={[Autoplay({ delay: 3500, stopOnInteraction: true })]} className="w-full">
            <CarouselContent className="-ml-4">
              {products.map((product, i) => (
                <CarouselItem key={product.id} className="pl-4 basis-[85%] sm:basis-1/2 lg:basis-1/3">
                  <Link to={`/products/${makeSlug(product.name)}`} className="block group">
                    <div className="relative rounded-2xl overflow-hidden aspect-[16/9] bg-muted">
                      <img src={product.image_url} alt={product.image_alt || product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      <div className="absolute bottom-4 left-4 right-4">
                        <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white">{product.category}</span>
                        <h3 className="font-heading text-xl font-medium text-white mt-2">{product.name}</h3>
                      </div>
                    </div>
                  </Link>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>
      </section>

      {/* Search + Grid */}
      <section className="py-16 sm:py-24">
        <div className="container mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="max-w-md mx-auto mb-12 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 h-12 rounded-full border-border" data-testid="products-search" />
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {filtered.map((product, i) => (
              <Reveal key={product.id} delay={i * 80}>
                <Link to={`/products/${makeSlug(product.name)}`} className="block h-full" data-testid={`product-card-link-${i}`}>
                  <Card className="hover-float overflow-hidden border border-border rounded-2xl group card-shimmer relative h-full cursor-pointer" data-testid={`product-card-${i}`}>
                    <div className="aspect-[4/3] overflow-hidden bg-muted relative">
                      <img src={product.image_url} alt={product.image_alt || product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                      <div className="absolute top-3 left-3">
                        <span className="text-xs font-medium px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm text-primary shadow-sm">
                          {product.in_stock ? t('products.inStock') : t('products.outOfStock')}
                        </span>
                      </div>
                    </div>
                    <CardContent className="p-5 sm:p-6">
                      <span className="text-xs text-muted-foreground">{product.category}</span>
                      <h3 className="font-heading text-lg font-medium mt-1 group-hover:text-primary transition-colors">{product.name}</h3>
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{product.short_description}</p>
                      <span className="inline-flex items-center mt-4 text-sm font-medium text-primary group-hover:gap-2 transition-all" data-testid={`product-view-${i}`}>
                        {t('products.viewDetails')} <ArrowRight className="ml-1 w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              </Reveal>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-16 animate-fade-in">
              <p className="text-muted-foreground">No products found.</p>
              <Button variant="outline" className="mt-4 rounded-full" onClick={() => setSearch('')} data-testid="clear-search">Clear Search</Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
